import { FULL_PERCENT_BP } from '@split/core';
import { z } from 'zod';
import {
  idSchema,
  inputMinorUnitsSchema,
  isoDateSchema,
  isoDateTimeSchema,
  moneySchema,
  positiveMoneySchema,
  rateSchema,
} from './common';

export const EXPENSE_CATEGORIES = [
  'food',
  'groceries',
  'transport',
  'stay',
  'drinks',
  'fun',
  'utilities',
  'gifts',
  'health',
  'other',
] as const;
export const expenseCategorySchema = z.enum(EXPENSE_CATEGORIES);
export type ExpenseCategory = z.infer<typeof expenseCategorySchema>;

/** `{ memberId, amount }` in minor units of the expense currency (at most `MAX_AMOUNT_MINOR`). */
export const allocationSchema = z.object({ memberId: idSchema, amount: inputMinorUnitsSchema });
export type AllocationDto = z.infer<typeof allocationSchema>;

/** Same shape as `SplitSpec` in `@split/core`. Percent uses basis points (100 % = 10000). */
export const splitSpecSchema = z.discriminatedUnion('mode', [
  z.object({ mode: z.literal('equal'), members: z.array(idSchema).min(1) }),
  z.object({ mode: z.literal('exact'), amounts: z.array(allocationSchema).min(1) }),
  z.object({
    mode: z.literal('percent'),
    percents: z
      .array(z.object({ memberId: idSchema, basisPoints: z.int().min(0).max(FULL_PERCENT_BP) }))
      .min(1),
  }),
  z.object({
    mode: z.literal('shares'),
    /** Integer weights; scale halves to integers (1 and 0.5 → 2 and 1). */
    shares: z.array(z.object({ memberId: idSchema, shares: z.int().min(0) })).min(1),
  }),
]);
export type SplitSpecDto = z.infer<typeof splitSpecSchema>;

export const fxSourceSchema = z.enum(['ecb', 'manual']);
export type FxSource = z.infer<typeof fxSourceSchema>;

/**
 * Conversion frozen on an expense/settlement: `rate` = units of `currency` (the group currency
 * at creation time) per unit of the item's own currency. `null` on items already in the
 * group currency.
 */
export const fxSnapshotSchema = z.object({
  currency: z.string(),
  rate: z.string(),
  /** Date of the ECB table the rate came from; `null` for manual rates. */
  date: isoDateSchema.nullable(),
  source: fxSourceSchema,
});
export type FxSnapshotDto = z.infer<typeof fxSnapshotSchema>;

/**
 * Optional manual rate on create/edit ("Edit rate"). When omitted and the expense currency
 * differs from the group currency, the server uses the ECB rate for the expense date.
 */
export const manualFxSchema = z.object({ rate: rateSchema });

const expenseFieldsSchema = z.object({
  title: z.string().trim().max(100),
  category: expenseCategorySchema,
  date: isoDateSchema,
  note: z.string().trim().max(500).nullable(),
  /** Total, strictly positive, in the expense's own currency. */
  amount: positiveMoneySchema,
  /** Who paid; amounts must sum to `amount.amount`. */
  payers: z.array(allocationSchema).min(1),
  split: splitSpecSchema,
  /** `null` = use ECB; `{ rate }` = manual rate. */
  fx: manualFxSchema.nullable(),
});

/** `POST /api/groups/:id/expenses`. */
export const createExpenseRequestSchema = expenseFieldsSchema
  .partial({ title: true, category: true, note: true, fx: true })
  .extend({
    /** Optional client-generated id (offline-first); retries with the same id are idempotent. */
    id: idSchema.optional(),
  });
export type CreateExpenseRequest = z.input<typeof createExpenseRequestSchema>;

/**
 * `PATCH /api/expenses/:id`: any subset of fields. `expectedVersion` enables optimistic
 * concurrency (`409 VERSION_CONFLICT` if someone else edited first). Changing `amount`
 * usually requires sending `payers` and `split` too so they still add up.
 */
export const updateExpenseRequestSchema = expenseFieldsSchema.partial().extend({
  expectedVersion: z.int().min(1).optional(),
});
export type UpdateExpenseRequest = z.input<typeof updateExpenseRequestSchema>;

export const receiptSchema = z.object({
  /** Authenticated URL (`GET /api/expenses/:id/receipt`). */
  url: z.string(),
  contentType: z.string(),
  size: z.int(),
});
export type Receipt = z.infer<typeof receiptSchema>;

export const expenseSchema = z.object({
  id: idSchema,
  groupId: idSchema,
  title: z.string(),
  category: expenseCategorySchema,
  date: isoDateSchema,
  note: z.string().nullable(),
  amount: moneySchema,
  payers: z.array(allocationSchema),
  split: splitSpecSchema,
  /** Each participant's computed share (`computeShares`), same order as the split. */
  shares: z.array(allocationSchema),
  fx: fxSnapshotSchema.nullable(),
  /** `amount` in the group currency (with `fx`), `null` when no conversion applies. */
  convertedAmount: moneySchema.nullable(),
  receipt: receiptSchema.nullable(),
  createdBy: idSchema,
  createdAt: isoDateTimeSchema,
  updatedBy: idSchema.nullable(),
  updatedAt: isoDateTimeSchema,
  deletedBy: idSchema.nullable(),
  deletedAt: isoDateTimeSchema.nullable(),
  /** Starts at 1, +1 on every edit/delete/restore. */
  version: z.int(),
  /** Whether the caller may edit/delete it (creator, a payer, or a group admin). */
  canEdit: z.boolean(),
});
export type Expense = z.infer<typeof expenseSchema>;

export const expenseHistoryEntrySchema = z.object({
  id: idSchema,
  version: z.int(),
  action: z.enum(['created', 'edited', 'deleted', 'restored']),
  actorId: idSchema,
  at: isoDateTimeSchema,
  /** Changed fields with old → new values (JSON values as stored). Empty for created/deleted. */
  changes: z.array(z.object({ field: z.string(), from: z.unknown(), to: z.unknown() })),
});
export type ExpenseHistoryEntry = z.infer<typeof expenseHistoryEntrySchema>;

export const expenseDetailSchema = z.object({
  expense: expenseSchema,
  /** Newest first. */
  history: z.array(expenseHistoryEntrySchema),
});
export type ExpenseDetail = z.infer<typeof expenseDetailSchema>;

export const expenseResponseSchema = z.object({ expense: expenseSchema });
export type ExpenseResponse = z.infer<typeof expenseResponseSchema>;

/** Accepted receipt types for `PUT /api/expenses/:id/receipt` (raw body, max 5 MB). */
export const RECEIPT_CONTENT_TYPES = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/heic',
] as const;
export const RECEIPT_MAX_BYTES = 5 * 1024 * 1024;
